/**
 * Alert Component
 *
 * A notice on the page: `warning`, `success` and `info` draw the tone's tint
 * and line (the text stays the page's ink, the icon takes the tone), so no
 * feature hand-rolls a coloured box. Actions go in `AlertAction`, under the
 * text. `role` is "alert" unless the caller says otherwise — a notice that is
 * on the page from the start and announces nothing is `role="note"`.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-foreground',
        destructive:
          'text-destructive-accessible bg-card *:data-[slot=alert-description]:text-destructive-accessible [&>svg]:text-current',
        warning:
          'border-tone-amber-line bg-tone-amber-subtle text-foreground *:data-[slot=alert-description]:text-foreground [&>svg]:text-tone-amber',
        success:
          'border-tone-green-line bg-tone-green-subtle text-foreground *:data-[slot=alert-description]:text-foreground [&>svg]:text-tone-green',
        info: 'border-tone-blue-line bg-tone-blue-subtle text-foreground *:data-[slot=alert-description]:text-foreground [&>svg]:text-tone-blue',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

/** The notice's actions, under its text. */
function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-action"
      className={cn('col-start-2 mt-2 flex flex-wrap items-center gap-2', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
